import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AdminOutreachChannel } from '../../__generated__/globalTypes';
import Table from '../../components/Table';
import { openRowInNewContext } from '../../lib/openRowInNewContext';
import { accountsFamilyPath } from '../../paths';

import ChannelHealthBadge from './ChannelHealthBadge';
import {
  ATTENTION_STATES,
  attentionStateLabel,
  CHANNEL_NAMES,
  channelHealthDetail,
  filterAttention,
} from './channelHealthView';
import SegmentedButtons from './SegmentedButtons';

import type { AdminGetChannelHealthQuery } from './__generated__/ChannelHealthScene.gql';
import type { ISegmentedOption } from './SegmentedButtons';
import type { AdminChannelHealthState } from '../../__generated__/globalTypes';

type AttentionList = AdminGetChannelHealthQuery['adminGetChannelHealth']['attention'];
type AttentionItem = AttentionList['items'][number];

const CHANNEL_OPTIONS: ReadonlyArray<ISegmentedOption<AdminOutreachChannel | null>> = [
  { value: null, label: 'All' },
  { value: AdminOutreachChannel.Sms, label: CHANNEL_NAMES[AdminOutreachChannel.Sms] },
  { value: AdminOutreachChannel.Email, label: CHANNEL_NAMES[AdminOutreachChannel.Email] },
];

const STATE_OPTIONS: ReadonlyArray<ISegmentedOption<AdminChannelHealthState | null>> = [
  { value: null, label: 'All' },
  ...ATTENTION_STATES.map((state) => ({ value: state, label: attentionStateLabel(state) })),
];

interface IAttentionRowProps {
  item: AttentionItem;
}

/** One channel needing a look; the row opens the member's household in Accounts, a modifier click in a new tab. */
const AttentionRow = ({ item }: IAttentionRowProps) => {
  const navigate = useNavigate();
  const to = accountsFamilyPath(item.familyId);
  const detail = channelHealthDetail(item.health);

  return (
    <Table.Row onClick={(event) => openRowInNewContext(event, to, navigate)} className="cursor-pointer">
      {/* The link navigates on its own, so its click must not reach the row. */}
      <Table.Cell className="text-xs" onClick={(event) => event.stopPropagation()}>
        <Link
          to={to}
          className="text-fg-brand-primary hover:text-fg-brand-secondary underline-offset-2 hover:underline"
        >
          {item.displayName}
        </Link>
      </Table.Cell>
      <Table.Cell muted className="text-xs">
        {CHANNEL_NAMES[item.health.channel]}
      </Table.Cell>
      <Table.Cell mono className="text-xs">
        {item.endpoint}
      </Table.Cell>
      {/* The badge opens its tooltip on press, so its click must not reach the row either. */}
      <Table.Cell onClick={(event) => event.stopPropagation()}>
        <ChannelHealthBadge health={item.health} />
      </Table.Cell>
      <Table.Cell muted className="text-xs">
        {detail ?? '—'}
      </Table.Cell>
    </Table.Row>
  );
};

interface IChannelAttentionPanelProps {
  attention: AttentionList;
}

/**
 * The channels an operator should look at, in the report's order: undeliverable,
 * then paused, then failing, then spam reports, newest first within each. A
 * member's own opt-out and a carrier STOP are choices, so the report counts
 * them without listing them. The filters narrow the rows the report returned.
 */
const ChannelAttentionPanel = ({ attention }: IChannelAttentionPanelProps) => {
  const [channel, setChannel] = React.useState<AdminOutreachChannel | null>(null);
  const [state, setState] = React.useState<AdminChannelHealthState | null>(null);
  const rows = filterAttention(attention.items, { channel, state });

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-fg-secondary text-xs font-semibold uppercase tracking-wider">
          Needs attention <span className="text-fg-tertiary ml-1 tabular-nums">{attention.total}</span>
        </h2>
        <div className="flex items-center gap-4">
          <SegmentedButtons label="Channel" options={CHANNEL_OPTIONS} value={channel} onChange={setChannel} />
          <SegmentedButtons label="State" options={STATE_OPTIONS} value={state} onChange={setState} />
        </div>
      </div>

      {attention.truncated && (
        <div className="text-fg-tertiary text-xs italic">
          Showing the first {attention.items.length} of {attention.total}; the filters narrow only those.
        </div>
      )}

      {attention.total === 0 && (
        <div className="text-fg-tertiary text-xs italic">
          Nothing to look at: every channel is healthy, unverified, or opted out by the member&apos;s own choice.
        </div>
      )}

      {attention.total > 0 && rows.length === 0 && (
        <div className="text-fg-tertiary text-xs italic">No channel here matches these filters.</div>
      )}

      {rows.length > 0 && (
        <Table>
          <Table.Header sticky>
            <Table.Row>
              <Table.HeaderCell>Member</Table.HeaderCell>
              <Table.HeaderCell>Channel</Table.HeaderCell>
              <Table.HeaderCell>Sends to</Table.HeaderCell>
              <Table.HeaderCell>State</Table.HeaderCell>
              <Table.HeaderCell>Detail</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((item) => (
              <AttentionRow key={`${item.userId}-${item.health.channel}`} item={item} />
            ))}
          </Table.Body>
        </Table>
      )}
    </section>
  );
};

export default ChannelAttentionPanel;
